import { useCallback, useEffect, useState } from 'react'
import { client } from '../lib/api'
import { strikeWarningCopy } from '../../shared/message-moderation'
import type { AccountStandingSnapshot } from '../../shared/pairvelle'

const STANDING_LABEL: Record<AccountStandingSnapshot['standing'], string> = {
  good: 'Good standing',
  limited: 'Limited',
  suspended: 'Suspended',
  pending_review: 'Pending review',
}

const VISIBILITY_LABEL: Record<AccountStandingSnapshot['visibilityStatus'], string> = {
  searchable: 'Searchable',
  limited: 'Limited visibility',
  hidden: 'Hidden from search',
}

export function AccountStanding({ notify, onBack }: { notify: (msg: string) => void; onBack: () => void }) {
  const [snapshot, setSnapshot] = useState<AccountStandingSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [appealNoticeId, setAppealNoticeId] = useState<string | null>(null)
  const [appealText, setAppealText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await client.standing()
      setSnapshot(res.standing)
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [notify])

  useEffect(() => {
    void load()
  }, [load])

  const submitAppeal = async () => {
    if (!appealText.trim()) return
    setSubmitting(true)
    try {
      const res = await client.submitAppeal(appealText.trim(), appealNoticeId ?? undefined)
      notify(res.message)
      setAppealText('')
      setAppealNoticeId(null)
      await load()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="panel-page">
        <button className="text-button" onClick={onBack}>
          ← Back to settings
        </button>
        <div className="loading-center">
          <div className="spinner" />
        </div>
      </div>
    )
  }

  if (!snapshot) {
    return (
      <div className="panel-page">
        <button className="text-button" onClick={onBack}>
          ← Back to settings
        </button>
        <div className="empty">
          <h2>Unable to load account standing</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="panel-page">
      <button className="text-button" onClick={onBack}>
        ← Back to settings
      </button>
      <span className="eyebrow">Transparency</span>
      <h1>Account standing</h1>
      <p className="page-sub">
        Clear standards, visible enforcement, and an appeal process. We do not promise zero moderation mistakes — we
        promise published rules and recourse. No one is exiled in silence.
      </p>
      <p className="page-sub">
        <a href="#/legal/community">Read the full Safety &amp; Rules document →</a>
      </p>

      <div className="settings-card standing-summary">
        <div>
          <small>Status</small>
          <strong>{STANDING_LABEL[snapshot.standing]}</strong>
        </div>
        <div>
          <small>Search visibility</small>
          <strong>{VISIBILITY_LABEL[snapshot.visibilityStatus]}</strong>
        </div>
        <div>
          <small>Verification</small>
          <strong>{snapshot.verificationStatus === 'verified' ? 'Verified' : snapshot.verificationStatus}</strong>
        </div>
        <div>
          <small>Daily interest remaining</small>
          <strong>
            {snapshot.dailyInterestsRemaining} / {snapshot.dailyInterestLimit}
          </strong>
        </div>
      </div>

      <div className="standing-capabilities">
        <span className={snapshot.canMessage ? 'ok' : 'limited'}>{snapshot.canMessage ? '✓' : '✕'} Messaging</span>
        <span className={snapshot.canIntroduce ? 'ok' : 'limited'}>{snapshot.canIntroduce ? '✓' : '✕'} Introductions</span>
        <span className={snapshot.searchable ? 'ok' : 'limited'}>{snapshot.searchable ? '✓' : '✕'} Discoverable</span>
      </div>

      {snapshot.messageStrikeStreak > 0 && (
        <div className="settings-card">
          <h2>Message safety strikes</h2>
          <p>{strikeWarningCopy(snapshot.messageStrikeStreak, snapshot.messageStrikeLimit)}</p>
          {snapshot.messageSuspendedUntil && (
            <p className="muted">
              Messaging paused until {new Date(snapshot.messageSuspendedUntil).toLocaleString()}.
            </p>
          )}
        </div>
      )}

      <h2>Active limitations</h2>
      {snapshot.activeNotices.length === 0 ? (
        <p className="muted">No active enforcement notices.</p>
      ) : (
        <div className="notice-list">
          {snapshot.activeNotices.map((n) => (
            <article key={n.id} className="notice-card">
              <header>
                <strong>{n.summary}</strong>
                <span className="notice-category">{n.category}</span>
              </header>
              <p>{n.details || 'No additional details provided.'}</p>
              <dl className="notice-meta">
                <div>
                  <dt>Reason</dt>
                  <dd>{n.reasonCode}</dd>
                </div>
                {n.contentRef && (
                  <div>
                    <dt>Reference</dt>
                    <dd>{n.contentRef}</dd>
                  </div>
                )}
                <div>
                  <dt>Started</dt>
                  <dd>{new Date(n.startsAt).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{n.endsAt ? `Until ${new Date(n.endsAt).toLocaleString()}` : 'Indefinite — appealable'}</dd>
                </div>
              </dl>
              <button className="button secondary" onClick={() => setAppealNoticeId(n.id)}>
                Appeal this notice
              </button>
            </article>
          ))}
        </div>
      )}

      {(appealNoticeId || snapshot.standing !== 'good') && (
        <div className="settings-form" style={{ marginTop: 24 }}>
          <h2>{appealNoticeId ? 'Submit appeal' : 'Request review'}</h2>
          <div className="field">
            <label>Your message</label>
            <textarea
              className="input"
              rows={4}
              value={appealText}
              onChange={(e) => setAppealText(e.target.value)}
              placeholder="Explain why you believe this action should be reviewed…"
            />
          </div>
          <div className="modal-actions">
            <button className="button primary" onClick={submitAppeal} disabled={submitting || !appealText.trim()}>
              {submitting ? 'Submitting…' : 'Submit appeal'}
            </button>
            {appealNoticeId && (
              <button className="button secondary" onClick={() => setAppealNoticeId(null)}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <h2>Appeal history</h2>
      {snapshot.appeals.length === 0 ? (
        <p className="muted">No appeals submitted yet.</p>
      ) : (
        <div className="notice-list">
          {snapshot.appeals.map((a) => (
            <article key={a.id} className="notice-card">
              <header>
                <strong>{a.status}</strong>
                <span className="notice-category">{new Date(a.createdAt).toLocaleString()}</span>
              </header>
              {a.response && <p>{a.response}</p>}
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
